import { NavLink } from "react-router";
import {
  LayoutDashboard,
  List,
  GitBranch,
  Shield,
  FileText,
  LogOut,
  PieChart,
  Receipt,
  CalendarClock,
  FileBarChart,
  Settings as SettingsIcon,
  Users,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useAuth } from "@/contexts/AuthContext";

const NAV_ITEMS = [
  { label: "Overview", path: "/", icon: LayoutDashboard },
  { label: "Requests", path: "/requests", icon: List },
  { label: "Routing", path: "/routing", icon: GitBranch },
  { label: "Savings", path: "/savings", icon: PieChart },
  { label: "Billing", path: "/billing", icon: Receipt },
  { label: "Current Period", path: "/current-period", icon: CalendarClock },
  { label: "Statements", path: "/statements", icon: FileBarChart },
  { label: "Governance", path: "/governance", icon: Shield },
  { label: "Prompt Stability", path: "/prompt-stability", icon: FileText },
] as const;

export function Sidebar() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Every Member manages their own sign-in methods in Settings; its key tabs
  // and the Team page are admin-only. Hiding them is UX, not security — the
  // backend enforces the role.
  const navItems = [
    ...NAV_ITEMS,
    ...(user?.role === "admin" ? [{ label: "Team", path: "/team", icon: Users } as const] : []),
    { label: "Settings", path: "/settings", icon: SettingsIcon } as const,
  ];

  async function handleLogout() {
    await logout();
    queryClient.clear();
    navigate("/login");
  }

  return (
    <aside className="relative flex h-screen w-56 flex-col border-r border-line-subtle bg-surface">
      {/* Subtle top-left blue ambient — anchors brand */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-40"
        style={{
          background:
            "radial-gradient(circle at 0% 0%, color-mix(in srgb, var(--color-electric-blue) 14%, transparent), transparent 60%)",
        }}
      />

      <div className="relative flex items-center gap-2.5 px-4 py-5 border-b border-line-subtle">
        <div className="relative">
          <img
            src="/phantm-logo.png"
            alt="Phantm"
            className="h-7 w-7 object-contain"
          />
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-safe-green shadow-[0_0_8px_var(--color-safe-green)] animate-pulse-dot"
          />
        </div>
        <div className="flex flex-col leading-none">
          <span className="text-sm font-semibold text-fg tracking-tight">
            Phantm
          </span>
          <span className="mt-0.5 text-[10px] uppercase tracking-[0.14em] text-fg-faint">
            Gateway
          </span>
        </div>
      </div>

      <nav className="relative flex-1 px-2 py-3 space-y-0.5">
        {navItems.map(({ label, path, icon: Icon }) => (
          <NavLink
            key={path}
            to={path}
            end={path === "/"}
            className={({ isActive }) =>
              `group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
                isActive
                  ? "bg-electric-blue/10 text-electric-blue font-medium"
                  : "text-fg-muted hover:bg-surface-2 hover:text-fg"
              }`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full bg-electric-blue"
                  />
                )}
                <Icon
                  size={16}
                  className={
                    isActive
                      ? "text-electric-blue"
                      : "text-fg-faint group-hover:text-fg"
                  }
                />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="relative border-t border-line-subtle px-3 py-3 space-y-3">
        {user && (
          <div
            className="flex items-center gap-2 px-2 text-xs"
            title={user.email}
          >
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-electric-blue/15 text-[10px] font-semibold text-electric-blue uppercase">
              {user.email.charAt(0)}
            </div>
            <span className="truncate text-fg-muted">{user.email}</span>
          </div>
        )}
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-fg-muted transition-colors hover:bg-surface-2 hover:text-block-red"
        >
          <LogOut size={16} />
          Logout
        </button>
      </div>
    </aside>
  );
}
